import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from 'rxjs';

import { usePermissions, useUser } from '@work4all/data';

import { ERPTypes } from '@work4all/models/lib/Classes/ERPTypes.entity';
import { InboundInvoice } from '@work4all/models/lib/Classes/InboundInvoice.entity';
import { ProductionContract } from '@work4all/models/lib/Classes/ProductionContract.entity';
import { ReViewModel } from '@work4all/models/lib/Classes/ReViewModel.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { canEditERPStatus } from '@work4all/utils/lib/permissions';

import { ErpStatusPicker } from '../../../entity-picker/erp-status-picker/ErpStatusPicker';
import {
  mapErpStatus,
  mapFromErpStatus,
} from '../../../entity-picker/erp-status-picker/use-map-erp-status';
import {
  InvoiceStatusPicker,
  mapInvoiceStatus,
} from '../../../entity-picker/invoice-status-picker/InvoiceStatusPicker';
import { formatters } from '../../components/formatters';
import { PreviewContentField } from '../../components/PreviewContent';

interface UseErpPreviewFieldsProps {
  entity: Entities;
  entitiesData: (ERPTypes & InboundInvoice & ProductionContract)[];
}

export const useErpPreviewFields = (props: UseErpPreviewFieldsProps) => {
  const { entity, entitiesData = [] } = props;
  const user = useUser();

  const canEditStatus =
    canEditERPStatus(user) ||
    entitiesData.every((record) => !record.frozen && !record.isClosed);
  const { canEdit } = usePermissions();

  const { t } = useTranslation();
  return useMemo<PreviewContentField<ERPTypes>[]>(() => {
    if (entity === Entities.reViewModel) {
      return [
        {
          entity: Entities.user,
          accessor: 'user.displayName',
          label: t('MORE.EMPLOYEE'),
        },
        {
          accessor: 'invoiceDate',
          label: t('APPROVAL_CENTER.REVIEWMODEL.INVOICE_DATE'),
          formatter: formatters.dateSimple2year,
        },
        {
          entity: Entities.supplier,
          accessor: 'supplier.name',
          label: t('COMMON.SUPPLIER'),
          getData: (entry) => (entry as ReViewModel).supplier,
        },
        {
          entity: Entities.supplier,
          accessor: 'supplier.mainContacId',
          label: t('COMMON.CONTACT'),
          formatter: (_, entry) =>
            (entry as ReViewModel).supplier?.mainContact?.displayName,
        },
        {
          accessor: 'paymentStatus',
          label: t('COMMON.INBOUND_INVOICE.PAYMENT_STATUS'),
          formatter: (_, entry) =>
            t(`PAYMENT_STATUSES.${(entry as ReViewModel).paymentStatus}`),
        },
        {
          accessor: 'accountingStatus',
          label: t('COMMON.INBOUND_INVOICE.ACCOUNTING_STATUS'),
          formatter: (_, entry) =>
            t(`ACCOUNTING_STATUSES.${(entry as ReViewModel).accountingStatus}`),
        },
      ] as PreviewContentField<ERPTypes>[];
    } else if (entity === Entities.productionContract) {
      return [
        {
          entity: Entities.user,
          accessor: 'editor.displayName',
          label: t('MORE.EMPLOYEE'),
        },
        {
          accessor: 'productionDate',
          label: t('FIELDS.productionDate'),
          formatter: formatters.dateSimple2year,
        },
        {
          entity: Entities.businessPartner,
          accessor: 'businessPartner.data.name',
          label: t('COMMON.BUSINESSPARTNER'),
        },
        {
          entity: Entities.contact,
          accessor: 'contact.displayName',
          label: t('COMMON.CONTACT'),
          formatter: (_, entry) =>
            (entry as unknown as ProductionContract).contact?.displayName,
        },
        {
          entity: Entities.project,
          accessor: 'project',
          label: t('COMMON.PROJECT'),
          formatter: (_, entry) => entry.project?.name,
          getData: (entry) => entry.project,
          disabled: true,
        },
      ] as PreviewContentField<ERPTypes>[];
    } else {
      return [
        {
          accessor: 'note',
          label: t('MORE.EMPLOYEE'),
          formatter: (_, entry) => entry.user?.displayName,
        },
        {
          accessor: entity === Entities.contract ? 'contractDate' : 'date',
          label: t('COMMON.DATE'),
          formatter: formatters.dateSimple2year,
        },
        {
          entity: Entities.customer,
          accessor: 'businessPartnerId',
          label: (entry) =>
            entry.businessPartnerContactCombined?.businessPartner?.data
              .__typename === 'Kunde'
              ? t('COMMON.CUSTOMER')
              : t('COMMON.SUPPLIER'),
          getData: (entry) =>
            entry.businessPartnerContactCombined?.businessPartner?.data,
          formatter: (_, entry) =>
            entry.businessPartnerContactCombined?.businessPartner?.data.name,
        },
        {
          accessor: 'contactId',
          label: t('COMMON.CONTACT'),
          formatter: (_, entry) =>
            entry.businessPartnerContactCombined?.contact?.displayName,
        },
        {
          entity: Entities.project,
          accessor: 'project',
          label: t('COMMON.PROJECT'),
          formatter: (_, entry) => entry.project?.name,
          getData: (entry) => entry.project,
          disabled: true,
        },
        {
          accessor: 'frozen',
          label: t('COMMON.STATUS'),
          formatter: (_, entry) => {
            if (entity === Entities.invoice) {
              const status = mapInvoiceStatus(entry.ra, entry.paid);
              return t('COMMON.INVOICE_STATUS_PICKER_' + status);
            }
            const status = mapErpStatus(entry.frozen, entry.isClosed);
            return t('COMMON.ERP_STATUS_PICKER_' + status);
          },
          disabled:
            !canEditStatus ||
            entity === Entities.invoice ||
            !(entitiesData || []).every((record) =>
              canEdit({ entity, record })
            ),
          disabledReason: canEditStatus
            ? undefined
            : t('ERROR.NO_RIGHT_TO_EDIT_ERP_STATUS'),
          isCommon: (commonFields: string[]) => {
            if (entity === Entities.invoice)
              return (
                commonFields.includes('ra') && commonFields.includes('paid')
              );
            return (
              commonFields.includes('isClosed') &&
              commonFields.includes('frozen')
            );
          },
          getPicker: ({ entry, onEdit }) => {
            if (entity === Entities.invoice) {
              const status = mapInvoiceStatus(entry.ra, entry.paid);
              return (
                <InvoiceStatusPicker
                  multiple={false}
                  value={{
                    id: status,
                    name: t('COMMON.INVOICE_STATUS_PICKER_' + status),
                  }}
                  onChange={noop}
                />
              );
            }
            const status = mapErpStatus(entry.frozen, entry.isClosed);
            return (
              <ErpStatusPicker
                multiple={false}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value={{
                  id: status,
                  name: t('COMMON.ERP_STATUS_PICKER_' + status),
                }}
                onChange={(value) => {
                  const { frozen, isClosed } = mapFromErpStatus(value?.id);
                  onEdit({ frozen, isClosed });
                }}
              />
            );
          },
        },
      ];
    }
  }, [t, entity, canEditStatus, canEdit, entitiesData]);
};
